import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { User, Role, Team, Manager } from '@/types';
import { UserList } from './UserList';
import { ManagementAccessNotice } from './ManagementAccessNotice';
import { UserForm } from './UserForm';
import { DeleteUserDialog } from './DeleteUserDialog';
import { RemovedPeopleSection } from './RemovedPeopleSection';
import { canAdministerRoles } from '@/lib/rolesAdministration';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, RotateCcw, Search, Calendar, Mail, Trash2, Users, UserX, ShieldOff } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabaseClient';
import { usePermissions } from '@/contexts/PermissionContext';

interface DeletedUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  deleted_at: string;
  deleted_by: string | null;
  original_email: string;
  original_first_name: string;
  original_last_name: string;
  created_at: string;
}

interface UsersTabProps {
  users: User[];
  deletedUsers?: DeletedUser[];
  roles: Role[];
  teams: Team[];
  managers: Manager[];
  isLoading: boolean;
  deletedUsersLoading?: boolean;
  currentUserId?: string;
  currentUserRole?: string;
  onCreateUser?: (user: Omit<User, 'id'>) => Promise<{ success: boolean }>;
  onUpdateUser?: (user: User) => Promise<{ success: boolean }>;
  onDeleteUser?: (userId: string, isHardDelete?: boolean) => Promise<{ success: boolean }>;
  onEmailChange?: (email: string) => Promise<{ exists: boolean }>;
  onEmailCheck?: (email: string) => Promise<boolean>;
  onRefreshDeletedUsers?: () => void;
  onRefreshUsers?: () => void;
  addUserError?: string | null;
}

const UsersTab: React.FC<UsersTabProps> = React.memo(({
  users = [],
  deletedUsers = [],
  roles = [],
  teams = [],
  managers = [],
  isLoading = false,
  deletedUsersLoading = false,
  currentUserId,
  currentUserRole,
  onCreateUser,
  onUpdateUser,
  onDeleteUser,
  onEmailChange,
  onEmailCheck,
  onRefreshDeletedUsers,
  onRefreshUsers,
  addUserError,
}) => {
  const { toast } = useToast();
  
  // Dialog/modal state
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  /**
   * RBAC-4.1. A role changed from the record's Role card is saved already, but the list is refetched
   * only when the panel closes. Refetching at once put the list into its loading state, which
   * unmounted this tab and the open Edit panel with it, losing any unsaved profile edits (found
   * driving the screen, 2026-11-02).
   */
  const roleChangedWhileOpen = useRef(false);
  useEffect(() => {
    if (!isUserDialogOpen && roleChangedWhileOpen.current) {
      roleChangedWhileOpen.current = false;
      onRefreshUsers?.();
    }
  }, [isUserDialogOpen, onRefreshUsers]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  
  // Deleted users state
  const [deletedUsersSearchQuery, setDeletedUsersSearchQuery] = useState('');
  const [restoringUser, setRestoringUser] = useState<string | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  // Match Settings.backup.tsx: assume all users can manage users
  const { hasPermission, permissions } = usePermissions();
  // RBAC-4.3: who has had their access removed. removed_people() refuses anyone else (42501).
  const canSeeRemoved = canAdministerRoles(permissions);
  const [removedKey, setRemovedKey] = useState(0);
  const [removedCount, setRemovedCount] = useState<number | null>(null);
  const canManageUsers = hasPermission('manage_settings') || hasPermission('user.manage_all');
  const canResetPassword = hasPermission('system.override') || hasPermission('user.manage_all');

  // Component optimized to prevent unnecessary re-renders

  // Memoize team filter options to prevent recreation on every render
  const teamFilterOptions = useMemo(() => [
    { label: 'All Teams', value: 'all' },
    ...teams.map(team => ({
      label: team.name,
      value: team.id.toString()
    }))
  ], [teams]);

  // Handlers
  const handleAddUser = useCallback(() => {
    setEditingUser(null);
    setEmailError(null);
    setIsUserDialogOpen(true);
  }, []);

  const handleEditUser = useCallback((user: User) => {
    setEditingUser(user);
    setEmailError(null);
    setIsUserDialogOpen(true);
  }, []);

  const handleDeleteUser = useCallback((user: User) => {
    setDeletingUser(user);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCheckEmail = async (email: string): Promise<boolean> => {
    if (!onEmailCheck) return true;
    
    try {
      const exists = await onEmailCheck(email);
      if (exists) {
        setEmailError('A user with this email already exists.');
        return false;
      }
      setEmailError(null);
      return true;
    } catch (error) {
      console.error('Error checking email:', error);
      return true; // Allow submission if there's an error checking email
    }
  };

  const handleUserFormSubmit = async (userData: Omit<User, 'id'>): Promise<{ success: boolean }> => {
    if (!editingUser && userData.email) {
      const isEmailValid = await handleCheckEmail(userData.email);
      if (!isEmailValid) {
        return { success: false };
      }
    }

    setIsSubmitting(true);
    try {
      let result = { success: false };
      if (editingUser && onUpdateUser) {
        const updateData = { ...editingUser, ...userData };
        result = await onUpdateUser(updateData);
        // Note: Success toast is handled in the useUsers hook.
      } else if (onCreateUser) {
        result = await onCreateUser(userData);
        // Note: Success toast is handled in the useUsers hook.
      }
      
      if (result.success) {
        setIsUserDialogOpen(false);
      }
      
      return result;
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Error',
        description: 'Failed to save user. Please try again.',
        variant: 'destructive',
      });
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDeleteUser = async (isHardDelete: boolean = false) => {
    if (!deletingUser || !onDeleteUser) return;
    
    setIsDeleting(true);
    try {
      const result = await onDeleteUser(deletingUser.id!, isHardDelete);
      if (result.success) {
        const deleteType = isHardDelete ? 'permanently deleted' : 'deleted';
        toast({
          title: 'Success',
          description: `User ${deleteType} successfully!`,
        });
        setIsDeleteDialogOpen(false);
        setDeletingUser(null);
        // Refresh deleted users list (only for soft delete)
        if (!isHardDelete && onRefreshDeletedUsers) {
          onRefreshDeletedUsers();
        }
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const deleteType = isHardDelete ? 'permanently delete' : 'delete';
      toast({
        title: 'Error',
        description: `Failed to ${deleteType} user. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Deleted users handlers
  const handleRestoreUser = async (userId: string, newEmail: string) => {
    if (!newEmail || !newEmail.includes('@')) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setRestoringUser(userId);
    try {
      // Log the payload being sent to the Edge Function
      const payload = { userId, newEmail };
      // Sending restore user request to Edge Function
      const functionsBaseUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '/functions/v1';
      const response = await fetch(`${functionsBaseUrl}/restore-deleted-user`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to restore user');
      }

      toast({
        title: 'Success',
        description: `User restored successfully with email: ${newEmail}`,
      });

      setShowRestoreDialog(null);
      setNewEmail('');
      // Refresh both lists
      if (onRefreshDeletedUsers) {
        onRefreshDeletedUsers();
      }
      if (onRefreshUsers) {
        onRefreshUsers();
      }
    } catch (error: any) {
      console.error('Error restoring user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to restore user. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRestoringUser(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter deleted users based on search query
  const filteredDeletedUsers = deletedUsers.filter(user => 
    user.original_email.toLowerCase().includes(deletedUsersSearchQuery.toLowerCase()) ||
    `${user.original_first_name} ${user.original_last_name}`.toLowerCase().includes(deletedUsersSearchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {isLoading || !users || !teams ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/*
            SUB-3.14 (FR60). Above the list, because the list is where an account
            owner looks to answer "who can get at my customers' data". Renders
            nothing at all when no outside organization holds access, so an
            ordinary account is unchanged.
          */}
          <ManagementAccessNotice />

          {/* Hidden button that can be triggered from parent */}
          <Button
            onClick={handleAddUser}
            className="hidden"
            data-add-user-button
          >
            Add User
          </Button>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <div className="flex items-center justify-between">
              <TabsList className="bg-muted/50 border border-border h-auto p-1 overflow-visible">
                <TabsTrigger
                  value="active"
                  className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm py-1.5 whitespace-nowrap"
                >
                  <Users className="w-4 h-4" />
                  Active Users
                  <span className="bg-success/10 text-success px-2 py-1 rounded-full text-xs font-medium">
                    {users.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="deleted"
                  className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm py-1.5 whitespace-nowrap"
                >
                  <UserX className="w-4 h-4" />
                  Deleted Users
                  <span className="bg-destructive/10 text-destructive px-2 py-1 rounded-full text-xs font-medium">
                    {deletedUsers.length}
                  </span>
                </TabsTrigger>
                {canSeeRemoved && (
                  <TabsTrigger
                    value="removed"
                    className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm py-1.5 whitespace-nowrap"
                  >
                    <ShieldOff className="w-4 h-4" />
                    Access removed
                    {removedCount !== null && (
                      <span className="bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs font-medium">
                        {removedCount}
                      </span>
                    )}
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Search and Filter Controls - shown for active tab */}
              {activeTab === 'active' && (
                <div className="flex items-center space-x-4">
                  <SearchableSelect
                    id="team-filter"
                    value={teamFilter}
                    onValueChange={setTeamFilter}
                    options={teamFilterOptions}
                    placeholder="Filter by team"
                    className="w-48"
                  />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64"
                  />
                </div>
              )}

              {/* Search for deleted users tab */}
              {activeTab === 'deleted' && (
                <div className="flex items-center gap-3">
                  <div className="relative w-64">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <Input
                      type="search"
                      placeholder="Search inactive users..."
                      className="pl-10 h-10"
                      value={deletedUsersSearchQuery}
                      onChange={(e) => setDeletedUsersSearchQuery(e.target.value)}
                    />
                  </div>
                  {onRefreshDeletedUsers && (
                    <Button
                      onClick={onRefreshDeletedUsers}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 h-10"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Refresh
                    </Button>
                  )}
                </div>
              )}
            </div>

        {/* Active Users Tab */}
        <TabsContent value="active" className="space-y-4">
          <div className="bg-background rounded-lg shadow border">
            <UserList
              users={users}
              loading={isLoading}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              currentUserId={currentUserId}
              canEdit={canManageUsers}
              canDelete={canManageUsers}
              canSendAccessEmails={canResetPassword}
              teamFilter={teamFilter}
              searchQuery={searchQuery}
              typeFilter={typeFilter}
            />
          </div>
        </TabsContent>

        {/* RBAC-4.3 · Access removed: inactive, not deleted, restorable */}
        {canSeeRemoved && (
          // forceMount so the count on the tab is known before anyone opens it.
          <TabsContent value="removed" forceMount className="space-y-4 data-[state=inactive]:hidden">
            <div className="bg-background rounded-lg shadow border">
              <RemovedPeopleSection
                refreshKey={removedKey}
                onCount={setRemovedCount}
                onRestored={() => onRefreshUsers?.()}
              />
            </div>
          </TabsContent>
        )}

        {/* Inactive Users Tab */}
        <TabsContent value="deleted" className="space-y-4">
          {deletedUsersLoading ? (
            <div className="p-4 text-center">Loading inactive users...</div>
          ) : filteredDeletedUsers.length === 0 ? (
            <div className="p-4 text-center col-span-4 text-muted-foreground">
              {deletedUsersSearchQuery ? 'No inactive users match your search criteria.' : 'No inactive users found.'}
            </div>
          ) : (
            <div className="bg-background border rounded-md overflow-hidden">
                              <div className="grid grid-cols-4 gap-4 p-3 font-medium bg-muted text-sm">
                  <div>Name</div>
                  <div>Original Email</div>
                  <div>Inactive Date</div>
                  <div className="flex justify-end">Actions</div>
                </div>
              <div className="divide-y">
                {filteredDeletedUsers.map((user, idx) => (
                  <div
                    key={user.id}
                    className={`grid grid-cols-4 gap-4 p-3 text-sm items-center ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
                  >
                    <div className="flex items-center">
                      <span className="text-sm text-foreground">
                        {user.original_first_name} {user.original_last_name}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-foreground">
                      <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="truncate">{user.original_email}</span>
                    </div>
                    <div className="flex items-center text-sm text-foreground">
                      <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{formatDate(user.deleted_at)}</span>
                    </div>
                    <div className="flex justify-end space-x-2">
                      {showRestoreDialog === user.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Restore user?</span>
                          <Button
                            onClick={() => handleRestoreUser(user.id, user.original_email)}
                            disabled={restoringUser === user.id}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs h-8"
                          >
                            {restoringUser === user.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              'Restore'
                            )}
                          </Button>
                          <Button
                            onClick={() => setShowRestoreDialog(null)}
                            variant="ghost"
                            size="sm"
                            className="px-2 py-1 text-xs h-8"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowRestoreDialog(user.id)}
                          className="text-muted-foreground hover:text-primary focus:outline-none"
                          aria-label="Restore User"
                          title="Restore"
                          tabIndex={0}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
      </>
      )}

      <UserForm
        initialData={editingUser || undefined}
        roles={roles}
        managers={managers}
        allTeams={teams}
        onSubmit={handleUserFormSubmit}
        onCancel={() => setIsUserDialogOpen(false)}
        isSubmitting={isSubmitting}
        addUserError={addUserError || emailError}
        onEmailChange={onEmailChange}
        open={isUserDialogOpen}
        onOpenChange={setIsUserDialogOpen}
        currentUserRole={currentUserRole}
        canResetPassword={canResetPassword}
        onRoleChanged={() => {
          roleChangedWhileOpen.current = true;
        }}
        onAccessRemoved={() => {
          // They are off the active list now; closing the panel lets the deferred refetch run.
          setRemovedKey((k) => k + 1);
          setIsUserDialogOpen(false);
        }}
      />

      <DeleteUserDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        user={deletingUser}
        onConfirm={handleConfirmDeleteUser}
        isDeleting={isDeleting}
      />
    </div>
  );
});

UsersTab.displayName = 'UsersTab';

export default UsersTab;
